import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, can } from "../lib/api";
import { useStudio } from "../lib/context";
import { Search } from "lucide-react";
import { Badge, Empty, ErrorBox, errorMessage, Loading, PageHead, Seg } from "../components/ui";

/** Glossaire de traduction : fixe comment chaque terme technique est rendu (ou gardé en français) dans une langue locale. */
export function Glossary() {
  const { languages, teacher, toast } = useStudio();
  const local = languages.filter((l) => l.code !== "fr");
  const [lang, setLang] = useState(local[0]?.code ?? "wo");
  const [filter, setFilter] = useState("");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["glossary", lang], queryFn: () => api.glossary(lang) });
  const [form, setForm] = useState({ termFr: "", translation: "", keepFrench: false });
  const isReviewer = can.reviewLang(teacher, lang);
  const canEdit = can.author(teacher) || isReviewer;

  const save = useMutation({
    mutationFn: (t: { termFr: string; translation: string | null; keepFrench: boolean }) => api.putGlossary({ ...t, languageCode: lang }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["glossary", lang] });
      toast(r.status === "VALIDATED" ? "Terme enregistré et validé" : "Terme enregistré, en attente de relecture");
      setForm({ termFr: "", translation: "", keepFrench: false });
    },
    onError: (e) => toast(errorMessage(e), "err"),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (save.isPending || !form.termFr.trim() || (!form.keepFrench && !form.translation.trim())) return;
    save.mutate({ termFr: form.termFr.trim(), translation: form.keepFrench ? null : form.translation.trim(), keepFrench: form.keepFrench });
  };
  const f = filter.trim().toLowerCase();
  const rows = (q.data ?? []).filter((t) => !f || t.termFr.includes(f) || t.translation?.toLowerCase().includes(f));

  return (
    <>
      <PageHead kicker="Traduction" title="Glossaire" lead="Comment chaque terme technique est rendu, ou gardé en français, dans les langues du pays. Le tuteur et les traducteurs s'y réfèrent.">
        {local.length > 1 && <Seg label="Langue" value={lang} onChange={setLang} options={local.map((l) => ({ value: l.code, label: l.nativeName ?? l.name }))} />}
      </PageHead>

      {canEdit && (
        <form className="card row" style={{ marginBottom: 16, alignItems: "flex-end", flexWrap: "wrap", gap: 12 }} onSubmit={submit}>
          <label className="field" style={{ flex: "1 1 200px" }}><span>Terme en français</span><input className="input" value={form.termFr} maxLength={120} onChange={(e) => setForm({ ...form, termFr: e.target.value })} /></label>
          <label className="field" style={{ flex: "1 1 200px" }}><span>Traduction</span><input className="input" value={form.keepFrench ? "" : form.translation} disabled={form.keepFrench} maxLength={200} placeholder={form.keepFrench ? "Terme gardé en français" : ""} onChange={(e) => setForm({ ...form, translation: e.target.value })} /></label>
          <label className="check" style={{ height: 36 }}><input type="checkbox" className="switch" checked={form.keepFrench} onChange={(e) => setForm({ ...form, keepFrench: e.target.checked })} /> Garder en français</label>
          <button className="btn" disabled={save.isPending}>Enregistrer</button>
          <div className="muted small" style={{ flexBasis: "100%" }}>{isReviewer ? "Vous relisez cette langue : vos termes sont validés directement." : "Vos termes seront relus par un relecteur de la langue."}</div>
        </form>
      )}

      {q.isLoading && <Loading />}
      {q.error && <ErrorBox error={q.error} onRetry={() => q.refetch()} />}
      {q.data && (
        <div className="card pad-0 appear">
          <div className="row" style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
            <h2>{q.data.length} terme{q.data.length > 1 ? "s" : ""}</h2><span className="spacer" />
            <div className="input-icon" style={{ width: 260 }}><Search size={15} /><input className="input" placeholder="Rechercher…" aria-label="Rechercher un terme" value={filter} onChange={(e) => setFilter(e.target.value)} /></div>
          </div>
          {rows.length === 0 ? <Empty title={`Aucun terme${f ? " pour cette recherche" : ""}.`} /> : (
            <table className="t">
              <thead><tr><th>Français</th><th>Traduction</th><th>Statut</th>{canEdit && <th />}</tr></thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id}>
                    <td><b>{t.termFr}</b></td>
                    <td>{t.keepFrench ? <span className="muted">gardé en français</span> : t.translation}</td>
                    <td><Badge status={t.status} /></td>
                    {canEdit && <td style={{ textAlign: "right" }}>
                      {isReviewer && t.status !== "VALIDATED" && <button className="btn sm" disabled={save.isPending} onClick={() => save.mutate({ termFr: t.termFr, translation: t.translation, keepFrench: t.keepFrench })}>Valider</button>}
                      <button className="btn sm ghost" style={{ marginLeft: 6 }} onClick={() => setForm({ termFr: t.termFr, translation: t.translation ?? "", keepFrench: t.keepFrench })}>Modifier</button>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
}
